import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { apiConfig } from '../auth/authConfig'

// Animated counter hook
function useCountUp(target, duration = 2000, delay = 0) {
    const [value, setValue] = useState(0)
    const started = useRef(false)

    useEffect(() => {
        if (target <= 0 || started.current) return
        started.current = true
        const startTime = performance.now() + delay
        const tick = (now) => {
            const elapsed = now - startTime
            if (elapsed < 0) { requestAnimationFrame(tick); return }
            const progress = Math.min(elapsed / duration, 1)
            const eased = 1 - Math.pow(1 - progress, 3) // ease-out cubic
            setValue(Math.round(eased * target))
            if (progress < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
    }, [target, duration, delay])

    return value
}

function formatNum(n) {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
    return n.toLocaleString()
}

// Investor display names by email (lowercase)
const INVESTOR_NAMES = {
    // Aviator internal
    'cbradham@aviator-energy.com': 'Connor',
    'thilton@aviator-energy.com': 'Tim',
    // Investors
    'nholeman@explorationland.com': 'Natalie',
    'campo@cougarpipe.com': 'Greg',
    'msimon@explorationland.com': 'Mark',
    'btz@explorationland.com': 'Burt',
    'michaelw@cougarpipe.com': 'Michael',
    'mmoores@verabank.com': 'Michael',
    'bodie@maximusoperating.com': 'Steve',
    'ejoseph@maximusoperating.com': 'Eric',
    'bo@toatruss.com': 'Bo',
    'jasonbryanjones@gmail.com': 'Jason',
    'kimberlybellpe@gmail.com': 'Kimberly',
    'rdswisher@etmworks.com': 'Randy',
    'jlpalko@vogelresources.com': 'Jennifer & Erich',
    'epalko@vogelresources.com': 'Erich',
    'erich.palko@gmail.com': 'Erich',
    'jlpalko@gmail.com': 'Jennifer',
    'celeste.bradham@gmail.com': 'Bradhams',
    'brad@fondrenlp.com': 'Brad',
    'tdrice50@gmail.com': 'Tracy',
    'treyrice89@gmail.com': 'Trey',
    'tarabradham@gmail.com': 'Tara',
    'tismith387@gmail.com': 'Tim',
    'blake.linnerud@gmail.com': 'Blake',
    'pbradham42@gmail.com': 'Parker',
    'jkmartin21@gmail.com': 'John',
    'kyle.walkerttu@gmail.com': 'Kyle',
    'preston.m.bradham@gmail.com': 'Preston',
    'caseytx23@msn.com': 'Casey',
}

function getInvestorName(account) {
    if (!account) return 'Investor'
    const email = (account.username || '').toLowerCase()
    if (INVESTOR_NAMES[email]) return INVESTOR_NAMES[email]
    // Fallback: try Azure AD display name, then email prefix
    if (account.name) return account.name.split(' ')[0]
    return email.split('@')[0] || 'Investor'
}

function LandingPage() {
    const navigate = useNavigate()
    const { accounts } = useMsal()
    const activeAccount = accounts[0]
    const [stats, setStats] = useState(null)
    const [visible, setVisible] = useState(false)

    // Fade-in trigger
    useEffect(() => {
        const t = setTimeout(() => setVisible(true), 100)
        return () => clearTimeout(t)
    }, [])

    // Fetch live stats
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const [mapRes, tankRes] = await Promise.all([
                    fetch(`${apiConfig.baseUrl}/map`),
                    fetch(`${apiConfig.baseUrl}/tanks/sales`)
                ])
                const mapData = await mapRes.json()
                const tankData = await tankRes.json()
                setStats({
                    totalWells: mapData.kpis?.mappedWellCount || 0,
                    activeWells: mapData.kpis?.activeWellCount || 0,
                    dailyOil: mapData.kpis?.prodOil || 0,
                    dailyGas: mapData.kpis?.prodGas || 0,
                    mtdSalesOil: tankData.kpis?.mtdSalesOilBBL || 0,
                    routes: mapData.routes?.length || 0,
                })
            } catch {
                // Silent fail — landing page still shows without live data
            }
        }
        fetchStats()
    }, [])

    const wellCount = useCountUp(stats?.totalWells || 0, 2200, 600)
    const oilRate = useCountUp(stats?.dailyOil || 0, 2400, 800)
    const gasRate = useCountUp(stats?.dailyGas || 0, 2400, 1000)
    const mtdSales = useCountUp(stats?.mtdSalesOil || 0, 2400, 1200)

    const firstName = getInvestorName(activeAccount)

    return (
        <div className="min-h-screen bg-slate-950 relative overflow-hidden">
            {/* Animated gradient background */}
            <div className="landing-gradient-bg" />

            {/* Subtle grid overlay */}
            <div className="absolute inset-0 opacity-[0.03]"
                style={{
                    backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
                    backgroundSize: '60px 60px'
                }}
            />

            {/* Content */}
            <div className={`relative z-10 min-h-screen flex flex-col transition-all duration-1000 ${visible ? 'opacity-100' : 'opacity-0'}`}>

                {/* Top bar */}
                <div className="flex items-center justify-between px-4 sm:px-12 pt-4 sm:pt-8">
                    <img src="/aviator-energy-design.png" alt="Aviator Energy" className="h-8 sm:h-16 w-auto landing-logo-glow" />
                    <div className="flex items-center gap-2 sm:gap-3">
                        <span className="text-xs sm:text-sm text-blue-200/70 font-medium hidden sm:inline">{activeAccount?.username}</span>
                        <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" title="Live data" />
                    </div>
                </div>

                {/* Hero */}
                <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 -mt-4 sm:-mt-8">
                    {/* Greeting */}
                    <div className={`text-center transition-all duration-1000 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <p className="text-blue-300/60 text-xs sm:text-base font-medium tracking-[0.3em] uppercase mb-2 sm:mb-3">
                            Investor Portal
                        </p>
                        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight">
                            Welcome, {firstName}
                        </h1>
                        <p className="mt-2 sm:mt-4 text-base sm:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
                            Real-time production intelligence across the Permian Basin
                        </p>
                    </div>

                    {/* Live KPI Strip */}
                    {stats && (
                        <div className={`mt-6 sm:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 w-full max-w-4xl transition-all duration-1000 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                            <div className="landing-stat-card">
                                <p className="landing-stat-label">Operated Wells</p>
                                <p className="landing-stat-value">{wellCount.toLocaleString()}</p>
                                <p className="landing-stat-sub">{stats.routes} routes</p>
                            </div>
                            <div className="landing-stat-card">
                                <p className="landing-stat-label">Daily Oil</p>
                                <p className="landing-stat-value">{formatNum(oilRate)}</p>
                                <p className="landing-stat-sub">BBL/d gross</p>
                            </div>
                            <div className="landing-stat-card">
                                <p className="landing-stat-label">Daily Gas</p>
                                <p className="landing-stat-value">{formatNum(gasRate)}</p>
                                <p className="landing-stat-sub">MCF/d gross</p>
                            </div>
                            <div className="landing-stat-card">
                                <p className="landing-stat-label">MTD Oil Sales</p>
                                <p className="landing-stat-value">{formatNum(mtdSales)}</p>
                                <p className="landing-stat-sub">BBL sold this month</p>
                            </div>
                        </div>
                    )}

                    {/* Feature pills */}
                    <div className={`mt-6 sm:mt-14 flex flex-wrap justify-center gap-2 sm:gap-4 transition-all duration-1000 delay-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        {[
                            { icon: '📊', label: 'Production Analytics', desc: 'Daily & monthly trends' },
                            { icon: '🗺️', label: 'Interactive Field Map', desc: 'Well locations & routes' },
                            { icon: '🛢️', label: 'Sales & Inventory', desc: 'Tank levels & tickets' },
                        ].map((f, i) => (
                            <div key={i} className="landing-feature-card">
                                <span className="text-xl sm:text-2xl">{f.icon}</span>
                                <div>
                                    <p className="text-xs sm:text-sm font-semibold text-white">{f.label}</p>
                                    <p className="text-[10px] sm:text-xs text-slate-400 mt-0.5">{f.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* CTA */}
                    <div className={`mt-6 sm:mt-14 transition-all duration-1000 delay-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="group relative px-8 sm:px-10 py-3 sm:py-4 bg-blue-600 hover:bg-blue-500 text-white text-base sm:text-lg font-semibold rounded-xl shadow-2xl shadow-blue-600/30 hover:shadow-blue-500/40 transition-all duration-300 hover:scale-[1.02]"
                        >
                            <span className="relative z-10 flex items-center gap-3">
                                Enter Portal
                                <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="px-4 sm:px-12 pb-4 sm:pb-8 flex items-center justify-between">
                    <p className="text-[10px] sm:text-xs text-slate-600">
                        &copy; {new Date().getFullYear()} Aviator Energy, LLC
                    </p>
                    <p className="text-[10px] sm:text-xs text-slate-600">
                        Data delayed 7 days
                    </p>
                </div>
            </div>
        </div>
    )
}

export default LandingPage
